import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

/**
 * CCC Waterfall Chart - Shows how Cash Conversion Cycle is built
 * Start at 0 → +DSO → +DIO → -DPO → = CCC
 */
export default function CCCWaterfallChart({ dso, dio, dpo, ccc }) {
    const getCssVar = (name, fallback) => {
        const val = getComputedStyle(document.body).getPropertyValue(name).trim();
        return val || fallback;
    };

    const hexToRgba = (hex, alpha) => {
        if (!hex) return `rgba(0, 0, 0, ${alpha})`;
        if (hex.startsWith('rgb')) return hex.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`);
        const clean = hex.replace('#', '');
        const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
        const num = parseInt(full, 16);
        const r = (num >> 16) & 255;
        const g = (num >> 8) & 255;
        const b = num & 255;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const chartGray = getCssVar('--chart-gray', '#CBD5E1');
    const chartRed = getCssVar('--chart-red', '#EF4444');
    const chartOrange = getCssVar('--chart-orange', '#FB923C');
    const chartGreen = getCssVar('--chart-green', '#22C55E');
    const chartBlue = getCssVar('--chart-blue', '#3B82F6');
    const textPrimary = getCssVar('--text-1', '#1f2937');
    const textMuted = getCssVar('--muted', '#9ca3af');
    const dsoVal = Number(dso) || 0;
    const dioVal = Number(dio) || 0;
    const dpoVal = Number(dpo) || 0;
    const cccVal = Number(ccc) || 0;

    // Build waterfall steps
    const labels = ['Start', 'DSO', 'DIO', 'DPO', 'CCC'];
    const base = [0, 0, dsoVal, dsoVal + dioVal, 0];
    const change = [0, dsoVal, dioVal, -dpoVal, cccVal];
    const colors = [
        hexToRgba(chartGray, 0.8),
        hexToRgba(chartRed, 0.85),
        hexToRgba(chartOrange, 0.85),
        hexToRgba(chartGreen, 0.85),
        hexToRgba(chartBlue, 0.9)
    ];

    // Cumulative values for tooltips
    const cumulative = [0, dsoVal, dsoVal + dioVal, dsoVal + dioVal - dpoVal, cccVal];

    const data = {
        labels,
        datasets: [
            {
                label: 'Base',
                data: base,
                backgroundColor: 'rgba(0,0,0,0)',
                stack: 'stack',
                borderSkipped: false,
            },
            {
                label: 'Days',
                data: change,
                backgroundColor: colors,
                borderColor: 'rgba(0,0,0,0.1)',
                borderWidth: 1,
                stack: 'stack',
                barThickness: 60,
            }
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            valueLabels: false,
            title: {
                display: true,
                text: 'Cash Conversion Cycle Build-up',
                font: { size: 16, weight: 'bold' }
            },
            tooltip: {
                callbacks: {
                    title: (items) => {
                        if (!items || !items.length || !items[0]) return '';
                        const idx = items[0].dataIndex;
                        const labelMap = {
                            0: 'Start',
                            1: 'DSO (Days Sales Outstanding)',
                            2: 'DIO (Days Inventory Outstanding)',
                            3: 'DPO (Days Payables Outstanding)',
                            4: 'CCC (Cash Conversion Cycle)'
                        };
                        return labelMap[idx] || items[0].label;
                    },
                    label: (ctx) => {
                        if (ctx.datasetIndex === 0) return null;
                        const idx = ctx.dataIndex;
                        const val = change[idx];
                        const cum = cumulative[idx];
                        if (idx === 0) return '';
                        if (idx === 4) return `Final CCC: ${cum.toFixed(1)} days`;
                        return [
                            `Change: ${val >= 0 ? '+' : ''}${val.toFixed(1)} days`,
                            `Running Total: ${cum.toFixed(1)} days`
                        ];
                    }
                },
                filter: (item) => item.datasetIndex === 1
            }
        },
        scales: {
            x: {
                stacked: true,
                grid: { display: false },
                ticks: {
                    autoSkip: false,
                    maxRotation: 0,
                    font: { size: 12, weight: '500' }
                }
            },
            y: {
                stacked: true,
                title: { display: true, text: 'Days', font: { size: 13, weight: '600' } },
                ticks: {
                    callback: (v) => `${v}d`,
                    font: { size: 11 }
                },
                grid: { color: hexToRgba(chartGray, 0.25) }
            }
        },
        layout: {
            padding: { top: 30, bottom: 10, left: 15, right: 15 }
        }
    };

    // Plugin to draw value labels
    const valueLabelPlugin = {
        id: 'valueLabelPlugin',
        afterDatasetsDraw(chart) {
            const { ctx } = chart;
            const meta = chart.getDatasetMeta(1);

            ctx.save();
            ctx.font = 'bold 14px system-ui, -apple-system';
            ctx.textAlign = 'center';
            ctx.fillStyle = textPrimary;

            meta.data.forEach((bar, i) => {
                if (i === 0) return; // Skip start
                const val = change[i];
                if (val === 0) return;

                // Position label above or below bar depending on value
                const isPositive = val >= 0;
                const y = isPositive ? bar.y - 10 : bar.base + 18;

                // Main value
                ctx.fillText(`${val >= 0 ? '+' : ''}${val.toFixed(1)}d`, bar.x, y);

                // Show cumulative only for middle bars (not for final CCC)
                if (i > 0 && i < 4) {
                    ctx.font = '10px system-ui';
                    ctx.fillStyle = textMuted;
                    const cumY = isPositive ? y - 16 : y + 14;
                    ctx.fillText(`(${cumulative[i].toFixed(1)}d)`, bar.x, cumY);
                    ctx.font = 'bold 14px system-ui';
                    ctx.fillStyle = textPrimary;
                }
            });

            ctx.restore();
        }
    };

    return (
        <div style={{ height: 420, width: '100%', backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 16 }}>
            <Bar data={data} options={options} plugins={[valueLabelPlugin]} />
        </div>
    );
}
